import {
  Combobox as ComboboxPrimative,
  ComboboxItem,
  ComboboxPopover,
  ComboboxProps as ComboboxPrimativeProps,
  ComboboxProvider,
  useComboboxStore,
} from "@ariakit/react/combobox";
import { cn, useCombinedRef } from "@fatbees/ui-utils";
import type { VariantProps } from "class-variance-authority";
import { omit } from "ramda";
import { ForwardedRef, forwardRef, useRef, useState } from "react";
import {
  dropdownMenuContentClasses,
  dropdownMenuItemClasses,
} from "./dropdown-menu";
import { inputVariants } from "./input";

const comboItemClasses = [
  ...dropdownMenuItemClasses,
  // These need to mirror the /^focus:/ items in dropdownMenuItemClasses
  "data-active-item:bg-accent",
  "data-active-item:text-accent-foreground",
];

export interface ComboboxProps
  extends
    Omit<ComboboxPrimativeProps, "size" | "onChange">,
    VariantProps<typeof inputVariants> {
  value?: string;
  onChange?: (event: { target: HTMLInputElement; type: "change" }) => void;
  suggestions: readonly string[];
  hasError?: boolean;
  // Provided by InputError
  errorId?: string;
}

export const Combobox = forwardRef(function Combobox(
  {
    value: specifiedValue,
    placeholder,
    className,
    onChange,
    suggestions = [],
    hasError,
    errorId,
    ...props
  }: ComboboxProps,
  forwardedRef: ForwardedRef<HTMLInputElement>,
) {
  const [internalValue, setInternalValue] = useState("");
  const store = useComboboxStore({
    value: specifiedValue ?? internalValue,
    setValue: (newValue) => {
      internalRef.current!.value = newValue;

      if (onChange) {
        onChange({ target: internalRef.current!, type: "change" });
      }
      setInternalValue(newValue);
    },
  });
  const valueParts = internalValue
    .split(/\s+/)
    .map((part) => part.toLowerCase());
  const matchingSuggestions = suggestions.filter(
    (suggestion) =>
      !valueParts.some((part) => !suggestion.toLowerCase().includes(part)),
  );
  const internalRef = useRef<HTMLInputElement>(null);
  const ref = useCombinedRef(internalRef, forwardedRef);

  return (
    <ComboboxProvider store={store}>
      <ComboboxPrimative
        ref={ref}
        placeholder={placeholder}
        className={cn(inputVariants(props), className)}
        aria-invalid={hasError ? true : undefined}
        aria-errormessage={errorId}
        {...omit(["size"], props)}
      />
      <ComboboxPopover
        gutter={4}
        sameWidth
        className={cn(
          dropdownMenuContentClasses,
          matchingSuggestions.length === 0 && "opacity-0 pointer-events-none",
        )}
      >
        {matchingSuggestions.map((suggestion) => (
          <ComboboxItem
            key={suggestion}
            className={cn(comboItemClasses)}
            value={suggestion}
          >
            {suggestion}
          </ComboboxItem>
        ))}
      </ComboboxPopover>
    </ComboboxProvider>
  );
});
